// frontend/src/modules/expenses/pages/ApprovalPoliciesPage.tsx
//
// Finance-facing configuration for expense approval ROUTING (migration 076).
//
// Before 076 the `approval_chain_id` came from the request body — the person
// spending the money chose whether the "over 5k needs the CFO" chain applied.
// Routing is now a server decision: these policies are what the API consults
// at submit time, and whatever a client sends is ignored.
//
// Server contract (api/modules/expenses/approval-policies.routes.ts) — every
// route, READS INCLUDED, is gated on `expenses.approve`:
//   GET    /api/expenses/approval-policies[?include_inactive=true] → { rows }
//   POST   /api/expenses/approval-policies      { name, chain_id, category_id?,
//            min_amount?, max_amount?, priority?, is_active? } → 201 { policy }
//   PATCH  /api/expenses/approval-policies/:id  (same fields, all optional) → { policy }
//   DELETE /api/expenses/approval-policies/:id  → 204 (hard delete; expenses keep
//            the chain they were routed to, not the policy that chose it)
//
// Refusals worth naming rather than swallowing:
//   400 invalid_band     — max_amount below min_amount
//   400 chain_not_found  — the chain is not an ACTIVE chain in this workspace
//   400 category_not_found

import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Trash2, X, Check, SlidersHorizontal, ArrowRight } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatMoney } from '../../../design-system/format';
import { VISUAL } from '../../../design-system/visual';
import { Eyebrow, GlassCard } from '../../../design-system/pds';
import { approvalPoliciesApi } from '../api';
import type { ApprovalChain, Category, ExpenseApprovalPolicy } from '../types';

/** The category picker's "applies to everything" sentinel — Radix Select
 *  cannot hold an empty string as a value, so null is encoded. */
const ANY_CATEGORY = '__any__';

export function ApprovalPoliciesPage() {
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';

  const [showInactive, setShowInactive] = React.useState(false);
  const policiesQ = useApi<{ rows: ExpenseApprovalPolicy[] }>(
    `/api/expenses/approval-policies${showInactive ? '?include_inactive=true' : ''}`,
    [showInactive],
  );
  const policies = policiesQ.data?.rows ?? [];

  // Only ACTIVE chains can be routed to — the server refuses an inactive one
  // with 400 `chain_not_found`, so the picker never offers them.
  const chainsQ = useApi<{ rows: ApprovalChain[] }>('/api/expenses/approval-chains');
  const chains = (chainsQ.data?.rows ?? []).filter((c) => c.is_active !== false);
  const categoriesQ = useApi<{ rows: Category[] }>('/api/expenses/categories');
  const categories = categoriesQ.data?.rows ?? [];

  const chainName = React.useMemo(() => {
    const m = new Map<string, string>();
    (chainsQ.data?.rows ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [chainsQ.data]);
  const categoryName = React.useMemo(() => {
    const m = new Map<string, string>();
    categories.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [categories]);

  const [editing, setEditing] = React.useState<ExpenseApprovalPolicy | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [togglingId, setTogglingId] = React.useState<string | null>(null);

  function band(p: ExpenseApprovalPolicy): string {
    const min = formatMoney(p.min_amount, currency);
    return p.max_amount == null ? `${min} and up` : `${min} – ${formatMoney(p.max_amount, currency)}`;
  }

  async function setActive(p: ExpenseApprovalPolicy, is_active: boolean) {
    setTogglingId(p.id);
    try {
      await approvalPoliciesApi.update(p.id, { is_active });
      toast.success(is_active ? `"${p.name}" is active` : `"${p.name}" paused`);
      policiesQ.reload();
    } catch (err: any) {
      toast.error('Update failed', { description: err.message });
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={
          <>
            <Link to="/dashboard/expenses" className="hover:text-foreground transition-colors">Expenses</Link>
            {' / Approval policies'}
          </>
        }
        title="Approval policies"
        description="Decide which approval chain an expense goes through, by amount and category. The server applies these at submit time — the person claiming the money never picks."
        actions={
          <Button size="default" className="gap-1.5" onClick={() => { setAdding(true); setEditing(null); }} disabled={adding}>
            <Plus className="h-3.5 w-3.5" /> New policy
          </Button>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-5">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-saffron"
          />
          <span className="text-[12.5px] font-semibold text-ink/65 dark:text-white/65">Show paused policies</span>
        </label>

        {adding && (
          <PolicyEditor
            chains={chains}
            categories={categories}
            currency={currency}
            onCancel={() => setAdding(false)}
            onSaved={() => { setAdding(false); policiesQ.reload(); }}
          />
        )}

        <GlassCard padding="p-0" className="overflow-hidden">
          {policiesQ.loading ? (
            <div className="py-16 text-center text-[13px] text-ink/50 dark:text-white/50">Loading…</div>
          ) : policiesQ.status === 403 ? (
            <NoAccessState
              title="You can't configure approval routing"
              description="Editing a policy decides who signs off on money, so it needs the expenses.approve permission. Ask an Owner to grant it from Settings → Access."
            />
          ) : policiesQ.error ? (
            <ErrorState title="Couldn't load approval policies" description={policiesQ.error} onRetry={policiesQ.reload} />
          ) : policies.length === 0 && !adding ? (
            <EmptyPolicies onAdd={() => setAdding(true)} hasChains={chains.length > 0} />
          ) : (
            <ul className="divide-y divide-ink/6 dark:divide-white/8">
              {policies.map((p) =>
                editing?.id === p.id ? (
                  <li key={p.id} className="px-5 py-4">
                    <PolicyEditor
                      policy={p}
                      chains={chains}
                      categories={categories}
                      currency={currency}
                      onCancel={() => setEditing(null)}
                      onSaved={() => { setEditing(null); policiesQ.reload(); }}
                    />
                  </li>
                ) : (
                  <motion.li
                    key={p.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={['flex items-start gap-4 px-5 py-4', p.is_active === false ? 'opacity-70' : ''].join(' ')}
                  >
                    <div className="h-10 w-10 rounded-2xl bg-coral/12 flex items-center justify-center shrink-0">
                      <SlidersHorizontal className="h-4 w-4 text-coral-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold text-ink dark:text-white">{p.name}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink/70 dark:text-white/70">
                        <span className="font-mono tabular-nums">{band(p)}</span>
                        <span className="text-ink/30 dark:text-white/30">·</span>
                        <span>{p.category_id ? (categoryName.get(p.category_id) ?? 'Unknown category') : 'Any category'}</span>
                        <ArrowRight className="h-3 w-3 text-ink/30 dark:text-white/30" />
                        <span className="font-semibold text-ink dark:text-white">
                          {chainName.get(p.chain_id) ?? 'Unknown chain'}
                        </span>
                      </p>
                      <p className="mt-1 text-[11.5px] text-ink/45 dark:text-white/45">
                        Priority {p.priority} — higher wins when two policies both match.
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {p.is_active === false ? (
                        <>
                          <span className="inline-flex items-center rounded-full bg-ink/8 dark:bg-white/10 text-ink/55 dark:text-white/55 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide">
                            Paused
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={togglingId === p.id}
                            onClick={() => setActive(p, true)}
                            className="rounded-full gap-1.5 text-[12px] font-semibold"
                          >
                            {togglingId === p.id ? 'Resuming…' : 'Resume'}
                          </Button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => { setEditing(p); setAdding(false); }}
                            className="text-[12px] font-semibold text-ink/65 dark:text-white/65 hover:text-coral-600 px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            disabled={togglingId === p.id}
                            onClick={() => setActive(p, false)}
                            className="text-[12px] font-semibold text-ink/65 dark:text-white/65 hover:text-coral-600 px-2 py-1 disabled:opacity-50"
                          >
                            {togglingId === p.id ? 'Pausing…' : 'Pause'}
                          </button>
                          <DeletePolicyButton policy={p} onDeleted={policiesQ.reload} />
                        </>
                      )}
                    </div>
                  </motion.li>
                ),
              )}
            </ul>
          )}
        </GlassCard>

        <p className="text-[12px] text-ink/45 dark:text-white/45 leading-relaxed">
          An expense that matches no policy keeps single-step approval — one sign-off by anyone who
          holds <code className="font-mono text-[11.5px]">expenses.approve</code>. Bands may overlap:
          when two policies match, the higher priority wins, then the higher minimum.
        </p>
      </div>
    </div>
  );
}

/* ───────────────────────────── inner bits ───────────────────────────── */

function PolicyEditor({
  policy, chains, categories, currency, onCancel, onSaved,
}: {
  policy?: ExpenseApprovalPolicy;
  chains: ApprovalChain[];
  categories: Category[];
  currency: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = React.useState(policy?.name ?? '');
  // With exactly one active chain there is nothing to choose — preselect it
  // rather than making every policy fail validation on an obvious field.
  const [chainId, setChainId] = React.useState(
    policy?.chain_id ?? (chains.length === 1 ? chains[0].id : ''),
  );
  const [categoryId, setCategoryId] = React.useState(policy?.category_id ?? ANY_CATEGORY);
  const [minAmount, setMinAmount] = React.useState(policy ? String(Number(policy.min_amount)) : '0');
  const [maxAmount, setMaxAmount] = React.useState(
    policy?.max_amount != null ? String(Number(policy.max_amount)) : '',
  );
  const [priority, setPriority] = React.useState(String(policy?.priority ?? 0));
  const [busy, setBusy] = React.useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!chainId) { toast.error('Pick the approval chain this routes to'); return; }
    const min = Number(minAmount || 0);
    const max = maxAmount.trim() === '' ? null : Number(maxAmount);
    if (!Number.isFinite(min) || min < 0) { toast.error('Minimum must be a non-negative number'); return; }
    if (max != null && (!Number.isFinite(max) || max < 0)) { toast.error('Maximum must be a non-negative number'); return; }
    // Mirrors the server's 400 `invalid_band` so the user is told before a round trip.
    if (max != null && max < min) { toast.error('Maximum must be at or above the minimum'); return; }
    const prio = Number(priority || 0);
    if (!Number.isInteger(prio)) { toast.error('Priority must be a whole number'); return; }

    const body = {
      name: name.trim(),
      chain_id: chainId,
      category_id: categoryId === ANY_CATEGORY ? null : categoryId,
      min_amount: min,
      max_amount: max,
      priority: prio,
    };
    setBusy(true);
    try {
      if (policy) {
        await approvalPoliciesApi.update(policy.id, body);
        toast.success('Policy updated');
      } else {
        await approvalPoliciesApi.create(body);
        toast.success('Policy created');
      }
      onSaved();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className={[VISUAL.bento, 'p-5 space-y-4'].join(' ')}>
      <div className="space-y-1.5">
        <Label htmlFor="policy-name" className="text-[11px] uppercase tracking-[0.12em]">Policy name</Label>
        <Input
          id="policy-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Large claims need the CFO"
          autoFocus
          required
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="policy-min" className="text-[11px] uppercase tracking-[0.12em]">
            Minimum amount ({currency})
          </Label>
          <Input
            id="policy-min"
            type="number"
            min="0"
            step="0.01"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            placeholder="0"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="policy-max" className="text-[11px] uppercase tracking-[0.12em]">
            Maximum amount ({currency})
          </Label>
          <Input
            id="policy-max"
            type="number"
            min="0"
            step="0.01"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            placeholder="Leave blank for no ceiling"
          />
        </div>
      </div>
      <p className="text-[11.5px] text-ink/50 dark:text-white/45 -mt-1">
        Both ends are inclusive. A blank maximum means the policy has no ceiling.
      </p>

      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.12em]">Category</Label>
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger aria-label="Category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_CATEGORY}>Any category</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.12em]">Route to chain</Label>
        <Select value={chainId || undefined} onValueChange={setChainId}>
          <SelectTrigger aria-label="Route to chain"><SelectValue placeholder="Pick an approval chain…" /></SelectTrigger>
          <SelectContent>
            {chains.length === 0 ? (
              <SelectItem value="__none__" disabled>No active chains — create one first</SelectItem>
            ) : (
              chains.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
            )}
          </SelectContent>
        </Select>
        {chains.length === 0 && (
          <p className="text-[11.5px] text-ink/50 dark:text-white/45">
            <Link to="/dashboard/expenses/approval-chains" className="font-semibold underline underline-offset-4">
              Create an approval chain
            </Link>{' '}
            first — a policy has to route somewhere.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="policy-priority" className="text-[11px] uppercase tracking-[0.12em]">Priority</Label>
        <Input
          id="policy-priority"
          type="number"
          step="1"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          placeholder="0"
        />
        <p className="text-[11.5px] text-ink/50 dark:text-white/45">
          When two policies both match an expense, the higher priority wins.
        </p>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button type="button" variant="outline" size="default" onClick={onCancel} disabled={busy} className="gap-1.5">
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
        <Button type="submit" size="default" disabled={busy} className="gap-1.5">
          <Check className="h-3.5 w-3.5" />
          {busy ? 'Saving…' : policy ? 'Save policy' : 'Create policy'}
        </Button>
      </div>
    </form>
  );
}

function DeletePolicyButton({ policy, onDeleted }: { policy: ExpenseApprovalPolicy; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = React.useState(false);
  async function remove() {
    if (!(await confirm({
      title: `Delete the "${policy.name}" policy?`,
      description: 'Expenses already routed by it keep the chain they were given — only future claims are affected. Pause it instead if you might want it back.',
      destructive: true,
      confirmLabel: 'Delete',
    }))) return;
    setBusy(true);
    try {
      await approvalPoliciesApi.remove(policy.id);
      toast.success('Policy deleted');
      onDeleted();
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={remove}
      disabled={busy}
      aria-label={`Delete ${policy.name}`}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink/55 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50 transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function EmptyPolicies({ onAdd, hasChains }: { onAdd: () => void; hasChains: boolean }) {
  return (
    <div className="py-16 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-coral/12 flex items-center justify-center">
        <SlidersHorizontal className="h-6 w-6 text-coral-600" strokeWidth={1.6} />
      </div>
      <Eyebrow className="mb-2">No policies yet</Eyebrow>
      <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">
        Every claim gets one sign-off
      </p>
      <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
        With no policy configured, any expense can be approved by a single person who holds
        expenses.approve. Add a policy to send larger claims down a chain instead.
      </p>
      <div className="mt-6">
        {hasChains ? (
          <Button onClick={onAdd} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Create a policy</Button>
        ) : (
          <Link to="/dashboard/expenses/approval-chains">
            <Button className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Create an approval chain first</Button>
          </Link>
        )}
      </div>
    </div>
  );
}
